// File: frontend/src/views/public/TermsOfService.js

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertTriangle, CreditCard, Shield, Mail } from 'lucide-react';
import PageContainer from '../../components/layout/PageContainer';
import styles from './LegalPage.module.css';
import SEO from '../../components/utility/SEO';
import { SEO_CONFIG } from '../../config/seo';

const TermsOfService = () => {
  const seo = SEO_CONFIG.terms;

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO 
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.image}
      />
      <PageContainer>
        <div className={styles.page}>
          {/* Header */}
          <div className={styles.header}>
            <h1 className={styles.title}>Terms of Service</h1>
            <p className={styles.effectiveDate}>Effective Date: February 10, 2026</p>
            <p className={styles.intro}>
              Welcome to Allegory. These Terms of Service ("Terms") govern your access to and use of the Allegory digital insurance wallet platform, operated by Allegory Technology Inc. ("Allegory," "we," "us," or "our"). By creating an account or using our services, you agree to be bound by these Terms.
            </p>
            <p className={styles.intro}>
              <strong>Please read these Terms carefully. If you do not agree, you may not use our services.</strong>
            </p>
          </div>

          {/* Table of Contents */}
          <nav className={styles.toc}>
            <h2 className={styles.tocTitle}>Table of Contents</h2>
            <ol className={styles.tocList}>
              <li><button onClick={() => scrollToSection('section-1')}>Acceptance of Terms</button></li>
              <li><button onClick={() => scrollToSection('section-2')}>Description of Services</button></li>
              <li><button onClick={() => scrollToSection('section-3')}>Eligibility</button></li>
              <li><button onClick={() => scrollToSection('section-4')}>User Accounts</button></li>
              <li><button onClick={() => scrollToSection('section-5')}>Prohibited Uses</button></li>
              <li><button onClick={() => scrollToSection('section-6')}>User Content and Ownership</button></li>
              <li><button onClick={() => scrollToSection('section-7')}>Intellectual Property</button></li>
              <li><button onClick={() => scrollToSection('section-8')}>Payment Terms</button></li>
              <li><button onClick={() => scrollToSection('section-9')}>Disclaimers and Limitations of Liability</button></li>
              <li><button onClick={() => scrollToSection('section-10')}>Indemnification</button></li>
              <li><button onClick={() => scrollToSection('section-11')}>Termination</button></li>
              <li><button onClick={() => scrollToSection('section-12')}>Dispute Resolution and Governing Law</button></li>
              <li><button onClick={() => scrollToSection('section-13')}>Changes to These Terms</button></li>
              <li><button onClick={() => scrollToSection('section-14')}>Contact Us</button></li>
            </ol>
          </nav>

          {/* Content */}
          <div className={styles.content}>
            {/* Section 1 */}
            <section id="section-1" className={styles.section}>
              <h2 className={styles.sectionTitle}>1. Acceptance of Terms</h2>
              <p>
                By accessing or using Allegory's platform (the "Service"), you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
              <p>
                If you are using the Service on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className={styles.section}>
              <h2 className={styles.sectionTitle}>2. Description of Services</h2>
              <p>
                Allegory provides a digital insurance wallet platform that allows users to:
              </p>
              <ul>
                <li><strong>Store Insurance Policies:</strong> Upload and organize insurance policy documents (auto, home, life, health, pet, travel, commercial, pension) in a secure digital wallet</li>
                <li><strong>AI-Powered Policy Analysis:</strong> Use our artificial intelligence to extract policy details, identify coverage gaps, and answer questions about your policies</li>
                <li><strong>Claims Filing Assistance:</strong> Receive step-by-step guidance on filing insurance claims based on your specific policy terms</li>
                <li><strong>Renewal Alerts:</strong> Get notified 90, 60, and 30 days before your policies expire</li>
                <li><strong>Rate Comparison:</strong> Optionally request insurance quotes from carriers, brokers, and agents (with your explicit consent)</li>
              </ul>

              <div className={`${styles.disclaimer} ${styles.highlightSection}`}>
                <div className={styles.highlightBadge}>
                  <AlertTriangle size={24} strokeWidth={2} />
                </div>
                <h3 className={styles.subsectionTitle}>Important Disclaimer</h3>
                <p className={styles.highlightText}>
                  <strong>Allegory is not an insurance company.</strong> We do not sell, underwrite, or issue insurance policies. We do not provide legal, financial, or insurance advice. Our Service is a technology platform designed to help you understand and manage your existing insurance policies.
                </p>
                <p>
                  All insurance decisions, including purchasing, renewing, or filing claims, are your responsibility. Always verify information with your insurance carrier or a licensed insurance professional.
                </p>
              </div>
            </section>

            {/* Section 3 */}
            <section id="section-3" className={styles.section}>
              <h2 className={styles.sectionTitle}>3. Eligibility</h2>
              <p>
                To use our Service, you must:
              </p>
              <ul>
                <li>Be at least <strong>15 years old</strong> (or the minimum age required to hold a driver's license in your jurisdiction)</li>
                <li>If you are under 18, you must have parental or guardian consent to use the Service</li>
                <li>Be a resident of <strong>Canada or the United States</strong> (we do not currently offer services to users in other countries)</li>
                <li>Provide accurate and complete information during registration</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
              <p>
                By creating an account, you represent and warrant that you meet these eligibility requirements.
              </p>
            </section>

            {/* Section 4 */}
            <section id="section-4" className={styles.section}>
              <h2 className={styles.sectionTitle}>4. User Accounts</h2>

              <h3 className={styles.subsectionTitle}>4.1 Account Creation</h3>
              <p>
                To access our Service, you must create an account by providing your name, email address,
                and phone number. We use a passwordless authentication system that requires SMS verification.
              </p>

              <h3 className={styles.subsectionTitle}>4.2 SMS Verification</h3>
              <p>
                Allegory uses passwordless SMS-based authentication. SMS messages are sent in two scenarios:
              </p>
              <ul>
                <li>
                  <strong>Account Creation:</strong> During sign-up, you must check a mandatory consent
                  checkbox before your account is created. By checking this box, you provide express
                  written consent to receive a one-time passcode (OTP) via SMS for account verification.
                </li>
                <li>
                  <strong>Sign-In via Phone:</strong> If you choose to sign in using your phone number,
                  a consent notice is displayed before you submit your number. By continuing, you consent
                  to receive a one-time verification code via SMS for that sign-in attempt.
                </li>
              </ul>
              <p>
                SMS messages are sent only in direct response to actions you initiate. Message and data
                rates may apply. One message per sign-up or sign-in attempt. You may opt out at any time
                by replying <strong>STOP</strong> to any message from us. For help, reply{' '}
                <strong>HELP</strong> or email{' '}
                <a href="mailto:support@allegoryinsurance.com">support@allegoryinsurance.com</a>.
              </p>
              <div className={styles.highlight}>
                <p>
                  <strong>Important:</strong> Because our authentication system relies on SMS verification,
                  opting out of SMS means you will no longer be able to sign in or recover your account
                  using your phone number.
                </p>
              </div>

              <h3 className={styles.subsectionTitle}>4.3 Account Security</h3>
              <p>
                You are responsible for:
              </p>
              <ul>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately if you suspect unauthorized access</li>
              </ul>
              <p>
                You may not share your account with others. If you subscribe to a paid plan, you may add
                authorized users as permitted by your subscription tier.
              </p>

              <h3 className={styles.subsectionTitle}>4.4 Account Accuracy</h3>
              <p>
                You agree to provide accurate, current, and complete information during registration and
                to update your information as needed. Providing false or misleading information may result
                in account termination.
              </p>
            </section>

            {/* Section 5 */}
            <section id="section-5" className={styles.section}>
              <h2 className={styles.sectionTitle}>5. Prohibited Uses</h2>
              <p>
                You agree not to use the Service for any unlawful or prohibited purpose. Specifically, you may not:
              </p>

              <h3 className={styles.subsectionTitle}>5.1 Fraudulent Activity</h3>
              <ul>
                <li>Upload fraudulent, fake, or altered insurance documents</li>
                <li>Misrepresent your identity or insurance coverage</li>
                <li>Use the Service to commit insurance fraud or any other illegal activity</li>
              </ul>

              <h3 className={styles.subsectionTitle}>5.2 System Abuse</h3>
              <ul>
                <li>Upload files containing viruses, malware, or malicious code</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Scrape, crawl, or reverse-engineer the platform</li>
                <li>Use automated tools (bots, scripts) to access the Service without our permission</li>
                <li>Overload or disrupt our servers or networks</li>
              </ul>

              <h3 className={styles.subsectionTitle}>5.3 AI Misuse</h3>
              <ul>
                <li>Use prompt injection or other techniques to manipulate our AI assistant</li>
                <li>Attempt to extract proprietary information or training data from our AI models</li>
                <li>Use the AI for purposes other than understanding your own insurance policies</li>
                <li>Generate executable code, malicious content, or illegal material using our AI</li>
              </ul>

              <h3 className={styles.subsectionTitle}>5.4 Content Violations</h3>
              <ul>
                <li>Upload content that is illegal, obscene, defamatory, or infringes on others' rights</li>
                <li>Upload content containing profanity, hate speech, or discriminatory language</li>
                <li>Use the Service to harass, threaten, or harm others</li>
              </ul>

              <p>
                We reserve the right to remove any content that violates these Terms and to terminate accounts engaged in prohibited activities.
              </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className={styles.section}>
              <h2 className={styles.sectionTitle}>6. User Content and Ownership</h2>
              
              <h3 className={styles.subsectionTitle}>6.1 Your Content</h3>
              <p>
                You retain ownership of all insurance policy documents and personal information you upload to the Service ("User Content"). By uploading User Content, you grant Allegory a limited, non-exclusive, royalty-free license to:
              </p>
              <ul>
                <li>Store, process, and analyze your policies to provide our services</li>
                <li>Use AI to extract policy details and answer your questions</li>
                <li>Display your policies and related information in your account dashboard</li>
              </ul>
              <p>
                This license terminates when you delete your account or remove specific content.
              </p>

              <h3 className={styles.subsectionTitle}>6.2 Accuracy of User Content</h3>
              <p>
                You are responsible for ensuring that the policies you upload are accurate and authentic. Our AI analysis is based on the documents you provide. If you upload incorrect or fraudulent documents, our insights may be inaccurate, and we bear no liability for such inaccuracies.
              </p>

              <h3 className={styles.subsectionTitle}>6.3 Verification Responsibility</h3>
              <p>
                You must review and verify all information displayed in your dashboard. If you notice errors in extracted policy data, you can correct them through self-service tools or by contacting support.
              </p>
            </section>

            {/* Section 7 */}
            <section id="section-7" className={styles.section}>
              <h2 className={styles.sectionTitle}>7. Intellectual Property</h2>
              
              <h3 className={styles.subsectionTitle}>7.1 Allegory's Intellectual Property</h3>
              <p>
                All content, features, and functionality of the Service (including but not limited to software, text, graphics, logos, AI models, and user interfaces) are owned by Allegory Technology Inc. and are protected by copyright, trademark, and other intellectual property laws.
              </p>
              <p>
                You may not copy, modify, distribute, sell, or reverse-engineer any part of the Service without our written permission.
              </p>

              <h3 className={styles.subsectionTitle}>7.2 AI-Generated Insights</h3>
              <p>
                AI-generated insights, analysis, and recommendations provided by the Service are owned by Allegory. However, you have a perpetual, non-exclusive license to use these insights for your personal insurance management.
              </p>

              <h3 className={styles.subsectionTitle}>7.3 Aggregated Data</h3>
              <p>
                We may aggregate and anonymize user data for internal analytics, product improvement, and industry research. This aggregated data cannot be traced back to individual users and is owned by Allegory.
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8" className={styles.section}>
              <h2 className={styles.sectionTitle}>8. Payment Terms</h2>
              
              <h3 className={styles.subsectionTitle}>8.1 Subscription Plans</h3>
              <p>
                Allegory offers the following subscription plans:
              </p>
              <ul>
                <li><strong>Free Forever:</strong> No cost, limited features</li>
                <li><strong>Smart Shopper:</strong> $9.99/month or $99.99/year</li>
                <li><strong>Complete Protection:</strong> $24.99/month or $249.99/year</li>
                <li><strong>Business Shield:</strong> $49.99/month or $499.99/year</li>
              </ul>
              <p>
                Pricing is subject to change. We will notify you at least 30 days before any price increase.
              </p>

              <h3 className={styles.subsectionTitle}>8.2 Billing and Auto-Renewal</h3>
              <p>
                Paid subscriptions are billed in advance on a monthly or yearly basis, depending on your selected plan. Your subscription will automatically renew at the end of each billing period unless you cancel.
              </p>
              <p>
                You can cancel your subscription at any time from your account settings. Cancellations take effect at the end of the current billing period.
              </p>

              <h3 className={styles.subsectionTitle}>8.3 Payment Processing</h3>
              <p>
                Payments are processed through Stripe. We do not store your credit card information. By providing payment information, you authorize us to charge your payment method for all fees incurred.
              </p>

              <h3 className={styles.subsectionTitle}>8.4 Failed Payments</h3>
              <p>
                If a payment fails, we will attempt to charge your payment method up to three times over the course of one week. If all attempts fail:
              </p>
              <ul>
                <li>Your subscription will be canceled</li>
                <li>Your account will be downgraded to the Free Forever plan</li>
                <li>You will retain access to your uploaded policies but lose access to paid features</li>
              </ul>

              <h3 className={styles.subsectionTitle}>8.5 Refunds</h3>
              <p>
                If you cancel your subscription, you may request a prorated refund for the unused portion of your billing period. Refund requests must be submitted within 30 days of cancellation.
              </p>
              <p>
                We do not offer refunds for partial months or for subscriptions canceled after the billing period has ended.
              </p>

              <h3 className={styles.subsectionTitle}>8.6 Taxes</h3>
              <p>
                All fees are exclusive of applicable taxes (GST, HST, sales tax, etc.). You are responsible for paying all taxes associated with your subscription.
              </p>
            </section>

            {/* Section 9 - HIGHLIGHTED */}
            <section id="section-9" className={`${styles.section} ${styles.highlightSection}`}>
              <div className={styles.highlightBadge}>
                <AlertTriangle size={24} strokeWidth={2} />
              </div>
              <h2 className={styles.sectionTitle}>9. Disclaimers and Limitations of Liability</h2>
              
              <h3 className={styles.subsectionTitle}>9.1 No Warranty</h3>
              <p className={styles.highlightText}>
                <strong>THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED.</strong> We do not guarantee that the Service will be uninterrupted, error-free, or secure.
              </p>

              <h3 className={styles.subsectionTitle}>9.2 AI Accuracy</h3>
              <p>
                Our AI assistant provides insights based on the policies you upload. However:
              </p>
              <ul>
                <li>AI responses are probabilistic and may not always be accurate</li>
                <li>AI models have training data cutoff dates and may not reflect the latest insurance products or regulations</li>
                <li>Niche or complex policies may be analyzed less accurately</li>
              </ul>
              <p>
                <strong>We assume no liability for incorrect AI analysis.</strong> You are responsible for verifying all information with your insurance carrier or a licensed professional.
              </p>

              <h3 className={styles.subsectionTitle}>9.3 Renewal Alerts</h3>
              <p>
                We will make reasonable efforts to send renewal alerts 90, 60, and 30 days before your policies expire. However, we are not liable if:
              </p>
              <ul>
                <li>Alerts are delayed or not delivered due to technical issues</li>
                <li>You miss a renewal deadline based on our alerts</li>
                <li>Your policy expires or lapses</li>
              </ul>
              <p>
                It is your responsibility to monitor your policy expiration dates.
              </p>

              <h3 className={styles.subsectionTitle}>9.4 Claims Guidance</h3>
              <p>
                Our claims filing assistance provides general guidance based on your policy terms. However:
              </p>
              <ul>
                <li>We do not file claims on your behalf</li>
                <li>We are not responsible if your claim is denied or delayed</li>
                <li>Underwriting and claims decisions are made solely by your insurance carrier</li>
              </ul>
              <p>
                Even if we believe our guidance is correct, your insurer may interpret policy terms differently or find reasons to deny coverage.
              </p>

              <h3 className={styles.subsectionTitle}>9.5 Rate Comparison</h3>
              <p>
                If you request insurance quotes through our platform, we share minimal information with carriers, brokers, and agents. However:
              </p>
              <ul>
                <li>We do not guarantee that you will receive quotes</li>
                <li>Quoted rates may change based on underwriting</li>
                <li>We are not responsible for the accuracy of quotes provided by third parties</li>
              </ul>

              <h3 className={styles.subsectionTitle}>9.6 Limitation of Liability</h3>
              <p className={styles.highlightText}>
                <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, ALLEGORY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICE.</strong>
              </p>
              <p>
                Our total liability to you for any claims arising from these Terms or your use of the Service shall not exceed the amount you paid to Allegory in the 12 months preceding the claim.
              </p>
            </section>

            {/* Section 10 */}
            <section id="section-10" className={styles.section}>
              <h2 className={styles.sectionTitle}>10. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Allegory Technology Inc., its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including legal fees) arising out of or related to:
              </p>
              <ul>
                <li>Your use of the Service</li>
                <li>Your violation of these Terms</li>
                <li>Your violation of any third-party rights</li>
                <li>Any fraudulent or illegal activity conducted through your account</li>
              </ul>
            </section>

            {/* Section 11 */}
            <section id="section-11" className={styles.section}>
              <h2 className={styles.sectionTitle}>11. Termination</h2>
              
              <h3 className={styles.subsectionTitle}>11.1 Termination by You</h3>
              <p>
                You may terminate your account at any time by:
              </p>
              <ul>
                <li>Deleting your account from account settings</li>
                <li>Contacting us at <a href="mailto:support@allegoryinsurance.com">support@allegoryinsurance.com</a></li>
              </ul>
              <p>
                Upon termination:
              </p>
              <ul>
                <li>Your account is immediately deactivated</li>
                <li>You have 30 days to reactivate your account if you change your mind</li>
                <li>After 30 days, all your data is permanently deleted</li>
              </ul>

              <h3 className={styles.subsectionTitle}>11.2 Termination by Allegory</h3>
              <p>
                We may suspend or terminate your account immediately, without notice, if:
              </p>
              <ul>
                <li>You violate these Terms</li>
                <li>You engage in fraudulent or illegal activity</li>
                <li>Your payment fails after three attempts (account downgraded to Free plan)</li>
                <li>We are required to do so by law</li>
              </ul>

              <h3 className={styles.subsectionTitle}>11.3 Effect of Termination</h3>
              <p>
                Upon termination for non-payment, your account will be downgraded to the Free Forever plan, and you will retain access to your uploaded policies but lose access to paid features.
              </p>
              <p>
                Upon termination for violations or fraudulent activity, your account and all data will be permanently deleted, and you will not be permitted to create a new account.
              </p>
            </section>

            {/* Section 12 */}
            <section id="section-12" className={styles.section}>
              <h2 className={styles.sectionTitle}>12. Dispute Resolution and Governing Law</h2>
              
              <h3 className={styles.subsectionTitle}>12.1 Governing Law</h3>
              <p>
                These Terms are governed by the laws of the Province of Ontario, Canada, without regard to conflict of law principles.
              </p>

              <h3 className={styles.subsectionTitle}>12.2 Jurisdiction</h3>
              <p>
                Any disputes arising out of or related to these Terms or your use of the Service shall be resolved in the courts of Ontario, Canada. You consent to the exclusive jurisdiction of these courts.
              </p>

              <h3 className={styles.subsectionTitle}>12.3 Informal Resolution</h3>
              <p>
                Before filing a lawsuit, you agree to attempt to resolve any dispute informally by contacting us at <a href="mailto:legal@allegoryinsurance.com">legal@allegoryinsurance.com</a>. We will work in good faith to resolve the issue within 30 days.
              </p>

              <h3 className={styles.subsectionTitle}>12.4 Class Action Waiver</h3>
              <p>
                You agree to resolve disputes with Allegory on an individual basis. You waive any right to participate in a class action lawsuit or class-wide arbitration.
              </p>
            </section>

            {/* Section 13 */}
            <section id="section-13" className={styles.section}>
              <h2 className={styles.sectionTitle}>13. Changes to These Terms</h2>
              <p>
                We may update these Terms from time to time to reflect changes in our services or legal requirements. When we make changes:
              </p>
              <ul>
                <li>We will update the "Effective Date" at the top of this page</li>
                <li>We will notify you via email if the changes are material</li>
                <li>Your continued use of the Service after changes take effect constitutes acceptance of the updated Terms</li>
              </ul>
              <p>
                If you do not agree to the updated Terms, you must stop using the Service and delete your account.
              </p>
            </section>

            {/* Section 14 */}
            <section id="section-14" className={styles.section}>
              <h2 className={styles.sectionTitle}>14. Contact Us</h2>
              <p>
                If you have questions or concerns about these Terms, please contact us:
              </p>
              <div className={styles.contactInfo}>
                <p><strong>Allegory Technology Inc.</strong></p>
                <p>Toronto Exchange Tower</p>
                <p>130 King Street West, Suite 1900</p>
                <p>Toronto, ON, M5X 1E3, Canada</p>
                <p>Email: <a href="mailto:legal@allegoryinsurance.com">legal@allegoryinsurance.com</a></p>
                <p>Support: <a href="mailto:support@allegoryinsurance.com">support@allegoryinsurance.com</a></p>
              </div>
            </section>
          </div>

          {/* Footer CTA */}
          <div className={styles.footerCTA}>
            <p>Questions about these terms?</p>
            <Link to="/contact" className={styles.ctaButton}>
              <Mail size={20} strokeWidth={2} />
              Contact Us
            </Link>
          </div>
        </div>
      </PageContainer>
    </>
  );
};

export default TermsOfService;